import { Loader } from '@googlemaps/js-api-loader'
import SmoothScroll from '../utils/_smooth-scroll'
import Swiper from 'swiper'
import ld from 'lodash'
let __ = ld.noConflict()

/* global mtGoogleMapsObject, google, jQuery */

const $ = jQuery

export default function InitMaps () {
  const WRAPPERS = document.querySelectorAll('.mt-google-maps')
  if (WRAPPERS.length > 0) {
    for (let i = 0; i < WRAPPERS.length; i++) {
      const mtGoogleMaps = new MtGoogleMaps(WRAPPERS[i])
      mtGoogleMaps.init()
    }
  }
}

class MtGoogleMaps {
  constructor (WRAPPER) {
    // Elements
    this.WRAPPER = WRAPPER
    this.MAP = this.WRAPPER.querySelector('.mt-google-maps__map')
    this.PAGE_CONTAINER = this.WRAPPER.querySelector('.mt-google-maps__container-inner')
    this.CARDS_WRAPPER = this.WRAPPER.querySelector('.mt-google-maps__items')
    this.CARDS = this.WRAPPER.querySelectorAll('.mt-item-card')
    this.SWIPER_ELEMENT = this.WRAPPER.querySelector('.swiper')

    // Defaults
    this.swiper = null
    this.markerData = this.MAP.getAttribute('data-markers')
    this.gmMarkers = []
    this.breakPoint = 1160
    this.hasIconMarkers = false
    this.zoomValueForLargeMarkers = 11
    this.initialActiveMarkerId = undefined
    this.screenWidth = this.getScreenWidth()
    this.zoom = parseInt(this.MAP.getAttribute('data-zoom') ?? 10)
    this.center = {
      lat: parseFloat(this.MAP.getAttribute('data-lat') ?? 55.6697847),
      lng: parseFloat(this.MAP.getAttribute('data-lng') ?? 8.5655886)
    }
    this.mobileCenter = {
      lat: parseFloat(this.MAP.getAttribute('data-lat-mobile') ?? 55.6697847),
      lng: parseFloat(this.MAP.getAttribute('data-lng-mobile') ?? 8.5655886)
    }
    this.markerSizes = {
      large: 84,
      small: 12
    }
  }

  /**
   * Initialize the Google Maps class
   */
  init () {
    this.loader = new Loader({
      apiKey: mtGoogleMapsObject.apiKey,
      version: 'weekly'
    })

    this.loader.load().then(() => {
      this.googleMap = new google.maps.Map(this.MAP, {
        center: this.center,
        zoom: this.zoom,
        mapId: mtGoogleMapsObject.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      })

      // Add custom panToWithOffset
      google.maps.Map.prototype.panToWithOffset = function (latLng, offsetX, offsetY) {
        const map = this
        const ov = new google.maps.OverlayView()
        ov.onAdd = function() {
          const proj = this.getProjection()
          const aPoint = proj.fromLatLngToContainerPixel(latLng)
          aPoint.x = aPoint.x + offsetX
          aPoint.y = aPoint.y + offsetY
          map.panTo(proj.fromContainerPixelToLatLng(aPoint))
        }
        ov.draw = function() {}
        ov.setMap(this)
      }

      // Run map methods
      this.addMarkers()
      this.addMarkerEvent()
      this.addZoomEvent()
      this.addMarkerElementClass()
      this.updateCenterOnResize()
      this.addCardEvents()
    })

    this.createSwiper()
  }

  /**
   * Map a click event to each card
   */
  addCardEvents () {
    if (this.CARDS.length === 0) {
      return
    }

    this.CARDS.forEach(CARD => {
      CARD.addEventListener('click', () => this.clickCard(CARD))
    })
  }

  /**
   * Update center point on resize
   */
  updateCenterOnResize () {
    window.addEventListener('resize', __.debounce(() => {
      if (this.screenWidth === this.getScreenWidth()) {
        return
      }
      this.googleMap.setCenter(this.getCenter())
      this.screenWidth = this.getScreenWidth()
    }, 300))
  }

  /**
   * Get the center point, based on screen width
   * @returns {*|{lng: number, lat: number}}
   */
  getCenter () {
    if (this.getScreenWidth() > this.breakPoint) {
      return this.center
    }
    return this.mobileCenter
  }

  /**
   * Add Google Maps markers for the map. In here we set:
   * - the initial active marker id
   * - whether there's an icon marker
   */
  addMarkers () {
    const map = this.googleMap
    const markers = JSON.parse(this.markerData)

    if (markers === null) {
      return
    }

    for (const [id, data] of Object.entries(markers)) {
      let options = this.getImageMarkerOptions(data)
      const { termId, markerImage, markerIcon, markerIconActive } = data

      // Set this.initialActiveMarkerId to the first marker's id
      if (this.initialActiveMarkerId === undefined) {
        this.initialActiveMarkerId = termId
      }

      // If the marker doesn't have a markerImage, then the marker must be an icon marker.
      // If that's the case, we update this.hasIconMarkers to true, for the remainder of the script to use.
      if (markerImage === undefined) {
        options = this.getIconMarkerOptions(data)
        this.hasIconMarkers = true
      }

      // Add markerIcon and markerIconActive for the icon markers.
      // This is used when the marker has the active class.
      if (markerIcon !== undefined && markerIconActive !== undefined) {
        options.markerIcon = markerIcon
        options.markerIconActive = markerIconActive
      }

      // Create and set the Google Maps marker
      const gmMarker = new google.maps.Marker(options)
      gmMarker.setMap(map)

      // Make the marker available for later use
      this.gmMarkers.push(gmMarker)
    }
  }

  /**
   * Get image marker options
   * @param data
   * @returns {{icon: ({scaledSize: google.maps.Size, origin: google.maps.Point, anchor: google.maps.Point, url: string}|{scaledSize: google.maps.Size, origin: google.maps.Point, anchor: google.maps.Point, labelOrigin: google.maps.Point, url: string}), position: {lng: number, lat: number}, label: {className: string, text: *}, id: number}}
   */
  getImageMarkerOptions (data) {
    const { title, lat, lng, termId, markerImage } = data
    const { large, small } = this.markerSizes

    // Set initial image size
    let scaledSize = new google.maps.Size(large, large)
    let size = new google.maps.Size(large, large)
    let anchor = new google.maps.Point(large / 2, large / 2)
    let labelOrigin = new google.maps.Point(large / 2, large)

    // Show a small image, if map in too much zoomed out, or if the map has icon markers.
    if (this.zoom < this.zoomValueForLargeMarkers || this.hasIconMarkers) {
      scaledSize = new google.maps.Size(small, small)
      size = new google.maps.Size(small, small)
      anchor = new google.maps.Point(small / 2, small / 2)
      labelOrigin = new google.maps.Point(small / 2, small)
    }

    const wrapperClasses = this.WRAPPER.classList;
    let classNames = 'mt-marker-title';

    if (wrapperClasses.contains('noTextUntilMobile')){
      classNames += ' mt-marker-title--hidden';
    }

    return {
      position: { lat: parseFloat(lat), lng: parseFloat(lng) },
      icon: {
        url: this.getImageUrl(markerImage, termId),
        scaledSize: scaledSize,
        size: size,
        origin: new google.maps.Point(0, 0),
        anchor: anchor,
        labelOrigin: labelOrigin
      },
      label: {
        text: title,
        className: classNames,
        visible: false  // Set label as hidden initially
      },
      id: parseInt(termId)
    }
  }

  /**
   * Get url for image
   *
   * @param imageUrl
   * @param id
   * @returns {string}
   */
  getImageUrl (imageUrl, id) {
    return `${imageUrl}?cls=mt-marker-image&id=${id}`
  }

  /**
   * Get icon marker options
   * @param data
   * @returns {{icon: {scaledSize: google.maps.Size, origin: google.maps.Point, anchor: google.maps.Point, url: string}, position: {lng: number, lat: number}, id: number}}
   */
  getIconMarkerOptions (data) {
    const { lat, lng, termId, markerIcon } = data

    return {
      position: { lat: parseFloat(lat), lng: parseFloat(lng) },
      icon: {
        url: this.getIconUrl(markerIcon, termId),
        scaledSize: new google.maps.Size(24, 24),
        size: new google.maps.Size(24, 24),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(12, 12)
      },
      id: parseInt(termId)
    }
  }

  /**
   * Get url for icon
   *
   * @param iconUrl
   * @param id
   * @returns {string}
   */
  getIconUrl (iconUrl, id) {
    return `${iconUrl}?cls=mt-marker-icon&id=${id}`
  }

  /**
   * Add event for marker
   */
  addMarkerEvent () {
    if (this.gmMarkers.length === 0) {
      return
    }

    this.gmMarkers.forEach(gmMarker => {
      gmMarker.addListener('click', () => this.clickMarker(gmMarker))
    })
  }

  /**
   * Add correct pin classes for html markers
   */
  addMarkerElementClass () {
    let initialized = false

    this.googleMap.addListener('tilesloaded', () => {
      if (initialized || this.gmMarkers.length === 0) {
        return
      }

      let imageMarkerIsActive = false
      let iconMarkerIsActive = false

      for (let i = 0; i < this.gmMarkers.length; i++) {
        const gmMarker = this.gmMarkers[i]
        const gmMarkerIcon = gmMarker.getIcon()
        const markerIcon = gmMarker.icon.url
        const isIconMarker = markerIcon.indexOf('mt-marker-icon') !== -1
        const htmlMarker = this.getHtmlMarker(gmMarker.id)

        if (!htmlMarker) {
          continue
        }

        // Add main class
        htmlMarker.classList.add('map-pin')

        // If the marker is an icon marker
        if (isIconMarker) {
          htmlMarker.classList.add('map-pin--icon')

          if (!iconMarkerIsActive) {
            // Set first icon marker to active
            htmlMarker.classList.add('map-pin--active')
            // Set first icon marker's activeIcon
            gmMarkerIcon.url = this.getIconUrl(gmMarker.markerIconActive, gmMarker.id)
            gmMarker.setIcon(gmMarkerIcon)
            iconMarkerIsActive = true
          }
          continue
        }

        // If the marker is an image marker
        htmlMarker.classList.add('map-pin--image')

        if (this.zoom < this.zoomValueForLargeMarkers) {
          htmlMarker.classList.add('map-pin--small')
        }

        if (!imageMarkerIsActive) {
          htmlMarker.classList.add('map-pin--active')
          imageMarkerIsActive = true
        }
      }

      initialized = true
    })
  }

  /**
   * Click event for marker
   *
   * @param gmMarker
   */
  clickMarker (gmMarker) {
    this.panToMarker(gmMarker)
    this.setActiveCard(gmMarker.id)
    this.setActiveMarker(this.getHtmlMarker(gmMarker.id), gmMarker)
    this.scrollToCard(gmMarker.id)
    this.setActiveSlideToClickedMarker(gmMarker.id)
  }

  /**
   * Set active swiper slide - from id
   *
   * @param id
   */
  setActiveSlideToClickedMarker (id) {
    if (this.CARDS.length === 0 || this.getScreenWidth() > this.breakPoint) {
      return
    }

    this.CARDS.forEach((CARD, i) => {
      if (parseInt(CARD.getAttribute('data-card-id')) === id) {
        this.swiper.slideTo(i)
      }
    })
  }

  /**
   * Click event for card
   *
   * @param CARD
   */
  clickCard (CARD) {
    const cardId = parseInt(CARD.getAttribute('data-card-id'))
    const htmlMarker = this.getHtmlMarker(cardId)
    const gmMarker = this.gmMarkers.find(gmMarker => gmMarker.id === cardId)
    this.setActiveCard(cardId)
    this.setActiveMarker(htmlMarker, gmMarker)
    this.panToMarker(gmMarker)

    if (this.getScreenWidth() <= this.breakPoint) {
      SmoothScroll($(this.WRAPPER))
    }
  }

  /**
   * Pans to the marker, with offset X
   *
   * @param gmMarker
   */
  panToMarker (gmMarker) {
    this.googleMap.panToWithOffset({ lat: gmMarker.position.lat(), lng: gmMarker.position.lng() }, this.getOffsetX(), 0)
  }

  /**
   * Get markers
   *
   * @returns {*[]}
   */
  getHtmlMarkers () {
    const htmlMarkers = []
    const markerImages = this.WRAPPER.querySelectorAll('img[src*="?cls=mt-marker-icon"], img[src*="?cls=mt-marker-image"]')
    if (markerImages.length > 0) {
      for (let i = 0; i < markerImages.length; i++) {
        htmlMarkers.push(markerImages[i])
      }
    }
    return htmlMarkers
  }

  /**
   * Get custom marker from id
   *
   * @param id
   * @returns {*}
   */
  getHtmlMarker (id) {
    const htmlMarkers = this.getHtmlMarkers()

    if (htmlMarkers.length === 0) {
      return null
    }

    // Check if src contains the id of current marker
    for (let i = 0; i < htmlMarkers.length; i++) {
      const htmlMarker = htmlMarkers[i]
      const urlObject = new URL(htmlMarker.src)
      const params = new URLSearchParams(urlObject.search)
      if (params.get('id') === id.toString()) {
        return htmlMarker.parentElement
      }
    }

    return null
  }

  /**
   * Calculate offset X
   * @returns {number}
   */
  getOffsetX () {
    const screenWidth = this.getScreenWidth()
    if (this.PAGE_CONTAINER && screenWidth > 1161) {
      const rect = this.PAGE_CONTAINER.getBoundingClientRect()
      const right = rect.width + (screenWidth - rect.right)
      const remaining = screenWidth - right // calculate width of visible part of the map
      const rightCenter = right + remaining / 2 // calculate rightCenter of visible part of the map
      return (rightCenter - (screenWidth / 2))
    }
    return 0
  }

  /**
   * Set the active marker, and remove active class from others
   * @param htmlMarker
   * @param gmMarker
   */
  setActiveMarker (htmlMarker, gmMarker) {
    if (!htmlMarker || typeof gmMarker !== 'object') {
      return
    }

    // Remove active classes from markers
    this.removeActiveClassFromMarkers()

    // If google.maps has icon markers, the marker with images will not be updated.
    const markerImg = htmlMarker.querySelector('img')
    if (markerImg && markerImg.src.indexOf('mt-marker-image') !== -1 && this.hasIconMarkers) {
      return
    }
    htmlMarker.classList.add('map-pin--active')

    // Update icon when icon is active
    this.setIconMarkerIconsToActiveIcons(gmMarker)
  }

  /**
   * Add active icons for active pins
   *
   * @param gmMarker
   */
  setIconMarkerIconsToActiveIcons (gmMarker) {
    if (typeof gmMarker !== 'object') {
      return
    }

    this.removeActiveIconFromMarkers()

    const icon = gmMarker.getIcon()
    if (typeof icon !== 'object' || gmMarker.icon.url.indexOf('mt-marker-image') !== -1) {
      return
    }

    icon.url = this.getIconUrl(gmMarker.markerIconActive, gmMarker.id)
    gmMarker.setIcon(icon)
  }

  /**
   * Remove active icons for pins
   */
  removeActiveIconFromMarkers () {
    if (this.gmMarkers.length === 0) {
      return
    }

    for (let i = 0; i < this.gmMarkers.length; i++) {
      const gmMarker = this.gmMarkers[i]
      const icon = gmMarker.getIcon()

      if (typeof icon !== 'object' || gmMarker.icon.url.indexOf('mt-marker-image') !== -1) {
        continue
      }

      // Reset the icons to their main icon image
      icon.url = this.getIconUrl(gmMarker.markerIcon, gmMarker.id)
      gmMarker.setIcon(icon)
    }
  }

  /**
   * Remove active class from markers
   */
  removeActiveClassFromMarkers () {
    const htmlMarkers = this.getHtmlMarkers()

    if (htmlMarkers.length === 0) {
      return
    }

    for (let i = 0; i < htmlMarkers.length; i++) {
      const htmlMarker = htmlMarkers[i]

      // If google.maps has icon markers, the marker with images will not be updated.
      if (htmlMarker.src.indexOf('mt-marker-image') !== -1 && this.hasIconMarkers) {
        continue
      }

      htmlMarker.parentElement.classList.remove('map-pin--active')
    }
  }

  /**
   * Set active card from id
   *
   * @param id
   */
  setActiveCard (id) {
    if (this.CARDS.length === 0) {
      return
    }

    this.CARDS.forEach(CARD => {
      const cardId = parseInt(CARD.getAttribute('data-card-id'))
      CARD.classList.remove('mt-item-card--active')
      if (cardId === id) {
        CARD.classList.add('mt-item-card--active')
      }
    })
  }

  /**
   * Scroll to card
   *
   * @param id
   */
  scrollToCard (id) {
    if (this.CARDS.length === 0) {
      return
    }

    let activeCard = null

    this.CARDS.forEach(CARD => {
      if (parseInt(CARD.getAttribute('data-card-id')) === id) {
        activeCard = CARD
      }
    })

    if (!activeCard) {
      return
    }

    this.CARDS_WRAPPER.scrollTo({
      top: activeCard.offsetTop - this.CARDS_WRAPPER.offsetTop,
      behavior: 'smooth'
    })
  }

  /**
   * Init swiper
   */
  createSwiper () {
    if (!this.SWIPER_ELEMENT) {
      return
    }

    let swiperInitialized = false

    const swiperOptions = {
      init: false,
      speed: 800,
      spaceBetween: 24,
      slidesPerView: 1.1,
      watchOverflow: true,
      grabCursor: true,
      breakpoints: {
        581: {
          slidesPerView: 1.5
        },
        851: {
          slidesPerView: 2
        }
      }
    }

    this.swiper = new Swiper(this.SWIPER_ELEMENT, swiperOptions)

    if (this.getScreenWidth() <= this.breakPoint) {
      this.swiper.init()
      swiperInitialized = true
    }

    window.addEventListener('resize', __.debounce(() => {
      if (this.getScreenWidth() <= this.breakPoint) {
        if (!swiperInitialized) {
          this.swiper = new Swiper(this.SWIPER_ELEMENT, swiperOptions)
          this.swiper.init()
          swiperInitialized = true
        }
      } else {
        if (swiperInitialized) {
          this.swiper.destroy(true, true)
          swiperInitialized = false
        }
      }
    }, 500))
  }

  /**
   * Add zoom event for google.maps. In here we:
   * - Update the markers to small version, if the zoom level less than this.zoomValueForLargeMarkers
   * - Update the markers to larger version, if the zoom level greater than this.zoomValueForLargeMarkers
   */
  addZoomEvent () {
    this.googleMap.addListener('zoom_changed', () => {
      // Don't update image markers, if the map has icon markers,as the image markers always should be small.
      if (this.hasIconMarkers) {
        return
      }

      if (this.googleMap.zoom < this.zoomValueForLargeMarkers) {
        this.updateMarkersToSmall()
        return
      }
      this.updateMarkersToLarge()
    })
  }

  /**
   * Update all markers to large version
   */
  updateMarkersToLarge () {
    if (this.gmMarkers.length === 0) {
      return
    }

    for (let i = 0; i < this.gmMarkers.length; i++) {
      const gmMarker = this.gmMarkers[i]

      // Don't update marker icons
      if (typeof gmMarker.icon === 'object' && gmMarker.icon.url.indexOf('mt-marker-icon') !== -1) {
        continue
      }

      this.updateMarkerIconToLarge(gmMarker)
    }

    // Check for additional classes and handle marker title visibility
    this.handleMarkerTitleVisibility();
  }

  /**
   * Update all markers to small version
   */
  updateMarkersToSmall () {
    if (this.gmMarkers.length === 0) {
      return
    }

    for (let i = 0; i < this.gmMarkers.length; i++) {
      const gmMarker = this.gmMarkers[i]

      // Don't update marker icons
      if (typeof gmMarker.icon === 'object' && gmMarker.icon.url.indexOf('mt-marker-icon') !== -1) {
        continue
      }

      this.updateMarkerIconToSmall(gmMarker)
    }

    // Check for additional classes and handle marker title visibility
    this.handleMarkerTitleVisibility();
  }

  /**
   * Update marker icon
   *
   * @param gmMarker
   */
  updateMarkerIconToLarge (gmMarker) {
    const icon = gmMarker.getIcon()
    if (typeof icon === 'object') {
      icon.anchor = new google.maps.Point(42, 42)
      icon.scaledSize = new google.maps.Size(84, 84)
      icon.size = new google.maps.Size(84, 84)
      icon.labelOrigin = new google.maps.Point(42, 84)
      gmMarker.setIcon(icon)
    }

    const htmlMarker = this.getHtmlMarker(gmMarker.id)
    if (htmlMarker) {
      htmlMarker.classList.remove('map-pin--small')
    }
  }

  /**
   * Update marker icon
   *
   * @param gmMarker
   */
  updateMarkerIconToSmall (gmMarker) {
    const icon = gmMarker.getIcon()
    if (typeof icon === 'object') {
      icon.anchor = new google.maps.Point(6, 6)
      icon.size = new google.maps.Size(12, 12)
      icon.scaledSize = new google.maps.Size(12, 12)
      icon.labelOrigin = new google.maps.Point(6, 12)
      gmMarker.setIcon(icon)
    }

    const htmlMarker = this.getHtmlMarker(gmMarker.id)
    if (htmlMarker) {
      htmlMarker.classList.add('map-pin--small')
    }
  }

  /**
   * Get screen width
   * @returns {number}
   */
  getScreenWidth () {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  }

  /**
   * Handle marker title visibility based on additional classes and zoom level
   */
  handleMarkerTitleVisibility () {
    const wrapperClasses = this.WRAPPER.classList;
    const markerTitles = this.WRAPPER.querySelectorAll('.mt-marker-title');


    if (wrapperClasses.contains('noTextUntilMobile') || wrapperClasses.contains('noTextOnMobile')) {
      markerTitles.forEach(title => {
        const zoom = this.googleMap.getZoom();
        if (
          (wrapperClasses.contains('noTextUntilMobile') && zoom < this.zoomValueForLargeMarkers) ||
          (wrapperClasses.contains('noTextOnMobile') && zoom >= this.zoomValueForLargeMarkers)
        ) {
          title.style.display = 'none';
        } else {
          title.style.display = 'block';
          title.classList.remove('mt-marker-title--hidden');
        }
      });
    }
  }
}
